import React, { useState, useEffect } from 'react';
import { Plus, CheckCircle, X, HelpCircle, Paperclip, Trash2, Download, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/DataTable/DataTable';
import Breadcrumb from '../../components/Common/Breadcrumb';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import HelpModal from '../../components/HelpModal/HelpModal';
import { burnExpenseService } from '../../services/burnExpenseService';
import { accountsService } from '../../services/accountsService';
import supplierMasterService from '../../services/supplierMasterService';
import fileUploadService from '../../services/fileUploadService';
import { toast } from 'react-toastify';
import '../../components/ConfirmationModal/ConfirmationModal.css';
import './BurnExpenseEntry.css';

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank', label: 'Bank' },
  { value: 'upi', label: 'UPI' },
  { value: 'card', label: 'Card' }
];

const ATTACH_ACCEPT = '.pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.svg';
const ATTACH_MIME = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/svg+xml',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const emptyForm = (categoryId = '') => ({
  expenseDate: new Date().toISOString().split('T')[0],
  categoryId,
  vendorId: '',
  vendorName: '',
  amount: '',
  paymentMode: 'bank',
  narration: '',
  isRecurring: false,
  cashAccountId: '',
  attachments: [],
});

const formatFileSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (n <= 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(n) / Math.log(k));
  return `${Math.round((n / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
};

const getCurrentMonth = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
};

const isAdmin = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const r = u?.role;
    if (Array.isArray(r)) return r.some(x => (x?.name || x?.roleName || '').toLowerCase() === 'admin');
    return (r?.name || r?.roleName || r || '').toString().toLowerCase().includes('admin');
  } catch {
    return false;
  }
};

const BurnExpenseEntry = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [filters, setFilters] = useState({ month: getCurrentMonth(), category: '', status: '', page: 1 });
  const [formData, setFormData] = useState(emptyForm());

  const canApprove = isAdmin();

  useEffect(() => {
    burnExpenseService.getCategories().then(r => {
      if (r?.success && r.data?.length) {
        setCategories(r.data);
        setFormData(f => ({ ...f, categoryId: f.categoryId || r.data[0]._id }));
      }
    });
    accountsService.getCashAccounts().then(r => {
      if (r?.success && r.data?.length) setCashAccounts(r.data);
    });
    supplierMasterService.getAllSuppliers({ status: 'Active', limit: 500 }).then((r) => {
      const list = r?.data || r?.suppliers || [];
      if (r?.success !== false && Array.isArray(list)) {
        setVendors(list.filter((s) => s && (s.status === 'Active' || !s.status)));
      }
    }).catch(() => {
      toast.error('Failed to load vendors from Supplier Master');
    });
  }, []);

  useEffect(() => {
    loadData();
  }, [filters.month, filters.category, filters.status, filters.page]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = { page: filters.page, limit: 20 };
      if (filters.month) params.month = filters.month;
      if (filters.category) params.category = filters.category;
      if (filters.status) params.status = filters.status;
      const res = await burnExpenseService.getEntries(params);
      if (res?.success) {
        setData((res.data || []).map(item => ({ ...item, id: item._id })));
      }
    } catch (err) {
      toast.error('Failed to load entries');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(emptyForm(categories[0]?._id || ''));
    setEditingItem(null);
  };

  const handleAdd = () => {
    resetForm();
    setShowForm(true);
  };

  const handleVendorChange = (vendorId) => {
    const selected = vendors.find((v) => String(v._id) === String(vendorId));
    setFormData((f) => ({
      ...f,
      vendorId: vendorId || '',
      vendorName: selected?.supplierName || '',
    }));
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    const vendorId = item.vendorId?._id || item.vendorId || '';
    setFormData({
      expenseDate: item.expenseDate ? new Date(item.expenseDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      categoryId: item.categoryId?._id || item.categoryId,
      vendorId: vendorId ? String(vendorId) : '',
      vendorName: item.vendorId?.supplierName || item.vendorName || '',
      amount: item.amount ?? '',
      paymentMode: item.paymentMode || 'bank',
      narration: item.narration || '',
      isRecurring: !!item.isRecurring,
      cashAccountId: item.cashAccountId?._id || item.cashAccountId || '',
      attachments: Array.isArray(item.attachments) ? item.attachments : [],
    });
    setShowForm(true);
  };

  const handleAttachmentUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be under 10 MB');
      event.target.value = '';
      return;
    }
    if (file.type && !ATTACH_MIME.includes(file.type)) {
      toast.error('Allowed: PDF, images, Word, Excel');
      event.target.value = '';
      return;
    }
    try {
      setUploadingFile(true);
      const result = await fileUploadService.uploadFile(file, 'burn-expense-attachment');
      if (result?.success && result.data) {
        const newAttachment = {
          fileName: result.data.originalname || file.name,
          filePath: result.data.filename,
          fileSize: result.data.size || file.size,
          mimeType: result.data.mimetype || file.type,
          uploadedDate: new Date(),
        };
        setFormData((prev) => ({
          ...prev,
          attachments: [...(prev.attachments || []), newAttachment],
        }));
        toast.success('Attachment uploaded');
      } else {
        toast.error(result?.message || 'Upload failed');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to upload attachment');
    } finally {
      setUploadingFile(false);
      event.target.value = '';
    }
  };

  const handleRemoveAttachment = (index) => {
    setFormData((prev) => ({
      ...prev,
      attachments: (prev.attachments || []).filter((_, i) => i !== index),
    }));
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      await fileUploadService.downloadFile(attachment.filePath);
    } catch {
      toast.error('Failed to download attachment');
    }
  };

  const handleDelete = (item) => {
    setDeleteItem(item);
    setShowDeleteModal(true);
  };

  const handleApprove = async (item) => {
    try {
      const res = await burnExpenseService.approveEntry(item._id);
      if (res?.success) {
        toast.success('Expense approved');
        loadData();
      } else {
        toast.error(res?.message || 'Failed to approve');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to approve');
    }
  };

  const handleSave = async () => {
    if (!formData.categoryId) {
      toast.error('Select a category');
      return;
    }
    const amt = parseFloat(formData.amount);
    if (isNaN(amt) || amt < 0) {
      toast.error('Enter valid amount');
      return;
    }
    try {
      const payload = {
        expenseDate: formData.expenseDate,
        categoryId: formData.categoryId,
        vendorId: formData.vendorId || null,
        vendorName: formData.vendorName || '',
        amount: amt,
        paymentMode: formData.paymentMode,
        narration: formData.narration,
        isRecurring: formData.isRecurring,
        attachments: formData.attachments || [],
      };
      if (formData.cashAccountId) payload.cashAccountId = formData.cashAccountId;
      else payload.cashAccountId = null;
      if (editingItem) {
        const res = await burnExpenseService.updateEntry(editingItem._id, payload);
        if (res?.success) {
          toast.success('Entry updated');
          setShowForm(false);
          loadData();
        } else toast.error(res?.message || 'Failed');
      } else {
        const res = await burnExpenseService.createEntry(payload);
        if (res?.success) {
          toast.success('Entry created');
          setShowForm(false);
          loadData();
        } else toast.error(res?.message || 'Failed');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save');
    }
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    try {
      const res = await burnExpenseService.deleteEntry(deleteItem._id);
      if (res?.success) {
        toast.success('Entry deleted');
        setShowDeleteModal(false);
        setDeleteItem(null);
        loadData();
      } else toast.error(res?.message || 'Failed');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed');
    }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(v || 0);

  const columns = [
    { accessorKey: 'expenseDate', header: 'Date', cell: ({ getValue }) => new Date(getValue()).toLocaleDateString() },
    { accessorKey: 'categoryId', header: 'Category', cell: ({ row }) => (row.original.categoryId?.name || row.original.categoryId) },
    { accessorKey: 'vendorName', header: 'Vendor', cell: ({ row }) => (row.original.vendorId?.supplierName || row.original.vendorName || '-') },
    { accessorKey: 'amount', header: 'Amount', cell: ({ getValue }) => formatCurrency(getValue()) },
    { accessorKey: 'paymentMode', header: 'Payment', cell: ({ getValue }) => String(getValue() || '').toUpperCase() },
    { accessorKey: 'cashAccountId', header: 'Cash Account', cell: ({ row }) => row.original.cashAccountId?.name || '-' },
    { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => <span className={`badge ${getValue() === 'approved' ? 'bg-success' : 'bg-warning'}`}>{getValue()}</span> },
    { accessorKey: 'isRecurring', header: 'Recurring', cell: ({ getValue }) => getValue() ? 'Yes' : 'No' },
    {
      accessorKey: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const r = row.original;
        return (
          <div className="d-flex gap-1">
            <button className="btn btn-sm btn-outline-primary" onClick={(e) => { e.stopPropagation(); handleEdit(r); }}>Edit</button>
            {canApprove && r.status === 'draft' && (
              <button className="btn btn-sm btn-success" onClick={(e) => { e.stopPropagation(); handleApprove(r); }}>
                <CheckCircle size={14} /> Approve
              </button>
            )}
            <button className="btn btn-sm btn-outline-danger" onClick={(e) => { e.stopPropagation(); handleDelete(r); }}>Delete</button>
          </div>
        );
      }
    }
  ];

  const breadcrumbItems = [
    { label: 'Expenses', path: '/expenses', onClick: () => navigate('/expenses') },
    { label: 'Transactions', path: '/expenses?tab=transactions', onClick: () => navigate('/expenses?tab=transactions') },
    { label: 'Burn Expense Entry', path: '/expenses/burn-entry', isCurrent: true }
  ];

  const helpContent = (
    <>
      <h3>What is Burn Expense Entry?</h3>
      <p>This screen is used to <span className="help-term">record and track expense burn</span>—the money your organisation spends on operations (utilities, hosting, salaries, stipends, etc.). Entries can be saved as draft and, once approved, can post to a cash account for accurate bookkeeping.</p>

      <h3>Navigation</h3>
      <p>Use the <span className="help-term">back arrow</span> to go to the previous page. The breadcrumb (Expenses &gt; Transactions &gt; Burn Expense Entry) shows where you are; click any part to jump back to that section.</p>

      <h3>Adding an entry</h3>
      <p>Click <span className="help-term">+ Add Entry</span> at the top right. In the form you can set:</p>
      <ul>
        <li><strong>Date</strong> – When the expense occurred</li>
        <li><strong>Category</strong> – Type of expense (e.g. Utilities, Server &amp; Hosting, Salary)</li>
        <li><strong>Vendor</strong> – Select from Supplier Master</li>
        <li><strong>Amount</strong> – Expense amount</li>
        <li><strong>Payment mode</strong> – Cash, Bank, UPI, or Card</li>
        <li><strong>Cash account</strong> – Optional; when the entry is approved, a debit is posted here</li>
        <li><strong>Narration</strong> – Optional notes</li>
        <li><strong>Attachments</strong> – Bills, invoices or receipts (PDF, images, Word, Excel)</li>
        <li><strong>Recurring</strong> – Mark if this expense repeats (e.g. monthly)</li>
      </ul>

      <h3>Filters and search</h3>
      <p>Use <span className="help-term">Month</span>, <span className="help-term">Category</span>, and <span className="help-term">Status</span> to narrow the list. The table also supports search to find entries quickly. The count shows how many entries are currently displayed.</p>

      <h3>Table columns</h3>
      <ul>
        <li><strong>Date</strong> – Expense date</li>
        <li><strong>Category</strong> – Expense category name</li>
        <li><strong>Vendor</strong> – Payee or &quot;-&quot; if not set</li>
        <li><strong>Amount</strong> – Expense amount (₹)</li>
        <li><strong>Payment</strong> – Payment mode (e.g. BANK)</li>
        <li><strong>Cash Account</strong> – Account used when approved, or &quot;-&quot;</li>
        <li><strong>Status</strong> – <em>Draft</em> (pending) or <em>Approved</em> (finalised)</li>
        <li><strong>Recurring</strong> – Yes/No for repeating expenses</li>
      </ul>

      <h3>Actions</h3>
      <p><span className="help-term">Edit</span> – Opens the form to change the entry. <span className="help-term">Approve</span> – (Admin only, for draft entries) marks the entry as approved and can post to the selected cash account. <span className="help-term">Delete</span> – Removes the entry after confirmation.</p>
    </>
  );

  const FormContent = () => (
    <div className="burn-entry-form">
      <div className="burn-entry-form__row">
        <div className="burn-entry-form__field">
          <label className="burn-entry-form__label">Date<span className="req">*</span></label>
          <input
            type="date"
            className="form-control"
            value={formData.expenseDate}
            onChange={(e) => setFormData((f) => ({ ...f, expenseDate: e.target.value }))}
          />
        </div>
        <div className="burn-entry-form__field">
          <label className="burn-entry-form__label">Amount<span className="req">*</span></label>
          <input
            type="number"
            step="0.01"
            min="0"
            className="form-control"
            value={formData.amount}
            onChange={(e) => setFormData((f) => ({ ...f, amount: e.target.value }))}
            placeholder="0.00"
          />
        </div>
      </div>

      <div className="burn-entry-form__field">
        <label className="burn-entry-form__label">Category<span className="req">*</span></label>
        <select
          className="form-select"
          value={formData.categoryId}
          onChange={(e) => setFormData((f) => ({ ...f, categoryId: e.target.value }))}
        >
          <option value="">Select category</option>
          {categories.map((c) => (
            <option key={c._id} value={c._id}>{c.name} ({c.group})</option>
          ))}
        </select>
      </div>

      <div className="burn-entry-form__row">
        <div className="burn-entry-form__field">
          <label className="burn-entry-form__label">Vendor</label>
          <select
            className="form-select"
            value={formData.vendorId}
            onChange={(e) => handleVendorChange(e.target.value)}
          >
            <option value="">Select vendor</option>
            {formData.vendorId && !vendors.some((v) => String(v._id) === String(formData.vendorId)) && (
              <option value={formData.vendorId}>
                {formData.vendorName || 'Selected vendor'}
              </option>
            )}
            {vendors.map((v) => (
              <option key={v._id} value={v._id}>
                {v.supplierName}{v.supplierCode ? ` (${v.supplierCode})` : ''}
              </option>
            ))}
          </select>
          <span className="burn-entry-form__hint">
            From Supplier Master. Add vendors under Procurement / Masters if missing.
          </span>
        </div>
        <div className="burn-entry-form__field">
          <label className="burn-entry-form__label">Payment Mode</label>
          <select
            className="form-select"
            value={formData.paymentMode}
            onChange={(e) => setFormData((f) => ({ ...f, paymentMode: e.target.value }))}
          >
            {PAYMENT_MODES.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="burn-entry-form__field">
        <label className="burn-entry-form__label">Cash Account (post on approve)</label>
        <select
          className="form-select"
          value={formData.cashAccountId}
          onChange={(e) => setFormData((f) => ({ ...f, cashAccountId: e.target.value }))}
        >
          <option value="">None</option>
          {cashAccounts.map((a) => (
            <option key={a._id} value={a._id}>{a.name} ({a.type || 'cash'})</option>
          ))}
        </select>
        <span className="burn-entry-form__hint">
          Optional. When approved, a debit will be posted to this cash account.
        </span>
      </div>

      <div className="burn-entry-form__field">
        <label className="burn-entry-form__label">Narration</label>
        <textarea
          className="form-control"
          rows={3}
          value={formData.narration}
          onChange={(e) => setFormData((f) => ({ ...f, narration: e.target.value }))}
          placeholder="Notes or description"
        />
      </div>

      <div className="burn-entry-form__field">
        <label className="burn-entry-form__label">Attachments</label>
        <div className="burn-entry-attach">
          <label className={`burn-entry-attach__btn${uploadingFile ? ' is-busy' : ''}`}>
            {uploadingFile ? <Loader2 size={14} className="spin" /> : <Paperclip size={14} />}
            <span>{uploadingFile ? 'Uploading…' : 'Add file'}</span>
            <input
              type="file"
              accept={ATTACH_ACCEPT}
              disabled={uploadingFile}
              onChange={handleAttachmentUpload}
              hidden
            />
          </label>
          <span className="burn-entry-form__hint">PDF, images, Word or Excel · max 10 MB</span>
        </div>
        {(formData.attachments || []).length > 0 && (
          <ul className="burn-entry-attach__list">
            {formData.attachments.map((attachment, index) => (
              <li key={`${attachment.filePath}-${index}`} className="burn-entry-attach__item">
                <div className="burn-entry-attach__meta">
                  <span className="burn-entry-attach__name" title={attachment.fileName}>
                    {attachment.fileName}
                  </span>
                  <span className="burn-entry-attach__size">{formatFileSize(attachment.fileSize)}</span>
                </div>
                <div className="burn-entry-attach__actions">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    title="Download"
                    onClick={() => handleDownloadAttachment(attachment)}
                  >
                    <Download size={14} />
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    title="Remove"
                    onClick={() => handleRemoveAttachment(index)}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="burn-entry-form__check">
        <input
          type="checkbox"
          className="form-check-input"
          id="burn-entry-recurring"
          checked={formData.isRecurring}
          onChange={(e) => setFormData((f) => ({ ...f, isRecurring: e.target.checked }))}
        />
        <label className="form-check-label" htmlFor="burn-entry-recurring">Recurring expense</label>
      </div>
    </div>
  );

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
              onClick={() => setShowHelp(true)}
              title="Help"
              aria-label="Open help"
            >
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>
      <div className="page-header">
        <div className="page-title">
          <h1>Burn Expense Entry</h1>
          <p>Record and track expense burn</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={handleAdd} disabled={!categories.length}><Plus size={16} /> Add Entry</button>
        </div>
      </div>

      <div className="mb-3 d-flex flex-wrap gap-2 align-items-center">
        <label className="form-label mb-0">Month:</label>
        <input type="month" className="form-control" style={{ width: 'auto' }} value={filters.month} onChange={(e) => setFilters(f => ({ ...f, month: e.target.value }))} />
        <label className="form-label mb-0">Category:</label>
        <select className="form-select" style={{ width: 'auto', minWidth: 160 }} value={filters.category} onChange={(e) => setFilters(f => ({ ...f, category: e.target.value }))}>
          <option value="">All</option>
          {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
        </select>
        <label className="form-label mb-0">Status:</label>
        <select className="form-select" style={{ width: 'auto', minWidth: 120 }} value={filters.status} onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}>
          <option value="">All</option>
          <option value="draft">Draft</option>
          <option value="approved">Approved</option>
        </select>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          noCard
          pageSize={20}
        />
      </div>

      {showForm && (
        <div
          className="confirmation-modal-overlay"
          onClick={(e) => e.target === e.currentTarget && (setShowForm(false), resetForm())}
        >
          <div
            className="confirmation-modal burn-entry-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="burn-entry-modal-title"
          >
            <div className="confirmation-modal-header">
              <h3 id="burn-entry-modal-title" className="confirmation-modal-title mb-0">
                {editingItem ? 'Edit Entry' : 'Add Entry'}
              </h3>
              <button
                type="button"
                className="confirmation-modal-close"
                onClick={() => (setShowForm(false), resetForm())}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="confirmation-modal-content">
              <FormContent />
            </div>
            <div className="confirmation-modal-footer">
              <button
                type="button"
                className="confirmation-modal-cancel"
                onClick={() => (setShowForm(false), resetForm())}
              >
                Cancel
              </button>
              <button type="button" className="btn btn-primary burn-entry-modal__save" onClick={handleSave}>
                {editingItem ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal isOpen={showDeleteModal} onClose={() => (setShowDeleteModal(false), setDeleteItem(null))} onConfirm={confirmDelete}
        title="Delete Entry" message="Delete this expense entry?" confirmText="Delete" type="danger" />

      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        title="Burn Expense Entry – Help"
      >
        {helpContent}
      </HelpModal>
    </div>
  );
};

export default BurnExpenseEntry;
